import { useState, type FormEvent } from 'react';
import { ArrowRight, Clock3, MapPinned, MessageCircleMore, Phone, ShieldCheck } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF } from '../../../lib/contact';

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: formData.get('name')?.toString() || '',
      phone: formData.get('phone')?.toString() || '',
      email: formData.get('email')?.toString() || '',
      service: formData.get('service')?.toString() || '',
      date: formData.get('date')?.toString() || '',
      message: formData.get('message')?.toString() || '',
      privacyAccepted: formData.get('privacyAccepted') === 'on',
      company: formData.get('company')?.toString() || '',
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      setSubmitStatus('success');
      form.reset();
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" aria-labelledby="contact-heading" className="bg-[#120f0d] py-20 text-[#f5eadb]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-10 lg:grid-cols-[0.92fr,1.08fr]">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/20 bg-[#1a1512] px-4 py-2 text-sm text-amber-300">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Kontakt und Anfrage
            </div>
            <div className="space-y-4">
              <h2 id="contact-heading" className="text-3xl font-semibold md:text-4xl">
                Akute Fälle direkt anrufen. Nicht akute Einsätze hier anfragen.
              </h2>
              <p className="max-w-2xl text-base leading-relaxed text-[#d9c9b8] md:text-lg">
                Wenn dein Fahrzeug akut Hilfe braucht, ist Telefon der direkte Weg. Das Formular ist für Rückrufwünsche,
                nicht zeitkritische Einsätze und allgemeine Fragen rund um Abschleppen, Bergen und Falschparker gedacht.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <a
                href={PHONE_HREF}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-300/40 bg-[#f2bf63] px-5 py-3 text-sm font-semibold text-[#1c140d] transition-colors hover:bg-[#ffcf82]"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_HREF}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2f2720] bg-[#1a1512] px-5 py-3 text-sm font-semibold text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
              >
                <MessageCircleMore className="h-4 w-4" aria-hidden="true" />
                WhatsApp
              </a>
              <a
                href="/leistungen"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2f2720] bg-[#1a1512] px-5 py-3 text-sm font-semibold text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
              >
                Leistungen
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>

            <div className="grid gap-3 text-sm text-[#d9c9b8] sm:grid-cols-3">
              <div className="rounded-[1rem] border border-[#2f2720] bg-[#1a1512] px-4 py-4">
                <div className="flex items-center gap-2 text-amber-300">
                  <Clock3 className="h-4 w-4" aria-hidden="true" />
                  <span className="font-semibold text-[#f5eadb]">24/7</span>
                </div>
                <p className="mt-2">Direkte Hilfe ohne Vermittlungsplattform.</p>
              </div>
              <div className="rounded-[1rem] border border-[#2f2720] bg-[#1a1512] px-4 py-4">
                <div className="flex items-center gap-2 text-amber-300">
                  <MapPinned className="h-4 w-4" aria-hidden="true" />
                  <span className="font-semibold text-[#f5eadb]">Berlin</span>
                </div>
                <p className="mt-2">Stadt, Bezirke und Umland nach Einsatzlage.</p>
              </div>
              <div className="rounded-[1rem] border border-[#2f2720] bg-[#1a1512] px-4 py-4">
                <div className="flex items-center gap-2 text-amber-300">
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  <span className="font-semibold text-[#f5eadb]">Sauber</span>
                </div>
                <p className="mt-2">Klare Zuständigkeit, dokumentierte Abwicklung.</p>
              </div>
            </div>
          </div>

          <div className="rounded-[1.2rem] border border-[#2f2720] bg-[#161311] p-6 shadow-[0_18px_60px_rgba(15,10,6,0.42)] md:p-7">
            <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit} noValidate autoComplete="on">
              <input type="text" name="company" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="md:col-span-2">
                <p className="text-sm leading-relaxed text-[#d9c9b8]">
                  Schick uns die Eckdaten. Für akute Fälle nutzen wir den Anrufweg, das Formular bleibt für Planung und Rückruf.
                </p>
              </div>

              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  Name*
                </label>
                <input
                  type="text"
                  required
                  id="name"
                  autoComplete="name"
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  name="name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  Telefon*
                </label>
                <input
                  type="tel"
                  required
                  id="phone"
                  autoComplete="tel"
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  name="phone"
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  E-Mail (optional)
                </label>
                <input
                  type="email"
                  id="email"
                  autoComplete="email"
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  name="email"
                />
              </div>

              <div>
                <label htmlFor="service" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  Anliegen*
                </label>
                <select
                  required
                  id="service"
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  name="service"
                >
                  <option value="">Bitte auswählen</option>
                  <option value="pannendienst">Pannendienst / Fahrzeug startet nicht</option>
                  <option value="unfall">Unfall / Bergung</option>
                  <option value="falschparker">Falschparker auf Privatgrund</option>
                  <option value="hof-tiefgarage">Fahrzeug in Hof / Tiefgarage</option>
                  <option value="motorrad-roller">Motorrad / Roller</option>
                  <option value="rueckruf">Rückrufwunsch / nicht akut</option>
                  <option value="sonstiges">Sonstiges Anliegen</option>
                </select>
              </div>

              <div>
                <label htmlFor="date" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  Datum &amp; Uhrzeit (optional)
                </label>
                <input
                  type="datetime-local"
                  id="date"
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  name="date"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-[#f5eadb]">
                  Nachricht
                </label>
                <textarea
                  rows={4}
                  id="message"
                  minLength={10}
                  className="w-full rounded-2xl border border-[#2f2720] bg-[#120f0d] px-4 py-3 text-[#f5eadb] outline-none transition placeholder:text-[#8f7f70] focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20"
                  placeholder="z. B. Standort, Fahrzeugtyp, Kennzeichen, was passiert ist, erreichbare Telefonnummer"
                  name="message"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-start gap-3 text-sm text-[#d9c9b8]">
                  <input
                    type="checkbox"
                    name="privacyAccepted"
                    required
                    className="mt-1 h-4 w-4 rounded border-[#2f2720] bg-[#120f0d] text-amber-300"
                  />
                  <span>
                    Ich habe die <a href="/datenschutz" className="underline decoration-amber-300/50 underline-offset-4 hover:text-amber-300">Datenschutzerklärung</a> gelesen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage einverstanden.
                  </span>
                </label>
              </div>

              <div className="md:col-span-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center rounded-full border border-amber-300/40 bg-[#f2bf63] px-8 py-3 text-sm font-semibold text-[#1c140d] transition-colors hover:bg-[#ffcf82] sm:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="md:col-span-2 text-sm text-amber-300" aria-live="polite" role="status">
                  Deine Anfrage wurde erfolgreich gesendet.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="md:col-span-2 text-sm text-[#f1b4a0]" aria-live="polite" role="alert">
                  Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
